/**
 * EditableQuadLabel.js
 * =====================
 * Renders a block label with a pencil button that lets the caregiver type a
 * custom replacement for the current session.  Used in HomePage and ResponsePage
 * for every content block (not the BACK bar).
 *
 * Font size auto-fits: starts at 34px and steps down until the text no longer
 * overflows the block height.
 *
 * Props
 * ─────
 * nodeId          – unique node ID (number or string) used as the override key
 * defaultLabel    – original label string from responseData
 * labelOverrides  – map { [nodeId]: customLabel } from GazeConfigContext
 * setLabelOverride – (nodeId, label) → void  from GazeConfigContext
 */

import React, { useState, useRef, useEffect } from 'react';
import './EditableQuadLabel.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatLines(text) {
  return (text || '').toUpperCase().split('\n').map((line, i, arr) => (
    <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
  ));
}

/**
 * Rough character-count estimate used as the initial font size to minimise
 * layout flash before the measurement effect runs.
 */
function estimateFontSize(text) {
  const len = (text || '').length;
  if (len <= 8)  return 34;
  if (len <= 14) return 28;
  if (len <= 22) return 22;
  if (len <= 35) return 17;
  return 14;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function EditableQuadLabel({ nodeId, defaultLabel, labelOverrides, setLabelOverride }) {
  const displayText = labelOverrides[String(nodeId)] ?? defaultLabel;

  const [editing,  setEditing]  = useState(false);
  const [draft,    setDraft]    = useState('');
  const [fontSize, setFontSize] = useState(() => estimateFontSize(displayText));

  const wrapRef     = useRef(null);
  const labelRef    = useRef(null);
  const textareaRef = useRef(null);

  // ── Auto-fit font size whenever the displayed text changes ────────────────
  // Finds the largest font size where the longest word fits within the block
  // width — ensures single words never break mid-character, and multi-word
  // labels wrap only at space boundaries.
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    const maxW = wrap.clientWidth - 24;
    const words = (displayText || '').toUpperCase().split(/\s+/).filter(Boolean);

    // Measure in a hidden off-screen span to avoid mutating the live label.
    const ruler = document.createElement('span');
    ruler.style.cssText =
      'position:absolute;visibility:hidden;white-space:nowrap;' +
      'font-family:"DM Sans",sans-serif;font-weight:400;padding:0;top:-9999px;left:-9999px;';
    document.body.appendChild(ruler);

    let chosen = 12;
    for (let size = 34; size >= 12; size -= 2) {
      ruler.style.fontSize = size + 'px';
      const allFit = words.every(word => {
        ruler.textContent = word;
        return ruler.scrollWidth <= maxW;
      });
      if (allFit) { chosen = size; break; }
    }

    document.body.removeChild(ruler);
    setFontSize(chosen);
  }, [displayText]);

  // ── Focus textarea on edit start ──────────────────────────────────────────
  useEffect(() => {
    if (editing) textareaRef.current?.focus();
  }, [editing]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  function startEdit(e) {
    e.stopPropagation();
    setDraft(displayText);
    setEditing(true);
  }

  function commit() {
    const trimmed = draft.trim();
    setLabelOverride(String(nodeId), trimmed || defaultLabel);
    setEditing(false);
  }

  // ── Editing state: full-block textarea ───────────────────────────────────
  if (editing) {
    return (
      <textarea
        ref={textareaRef}
        className="eql-textarea"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { setEditing(false); }
        }}
        onClick={e => e.stopPropagation()}
        placeholder="Type a label…"
        maxLength={80}
      />
    );
  }

  // ── Default state: label + pencil button ─────────────────────────────────
  return (
    <div ref={wrapRef} className="eql-wrap">
      <span ref={labelRef} className="quad-label" style={{ fontSize }}>
        {formatLines(displayText)}
      </span>
      <button
        className="eql-edit-btn"
        onClick={startEdit}
        aria-label={`Edit: ${displayText}`}
        tabIndex={-1}
      >
        {/* Pencil icon */}
        <svg viewBox="0 0 16 16" width="13" height="13" fill="currentColor" aria-hidden="true">
          <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207zm-7.468 7.468A.5.5 0 0 1 6 13.5V13h-.5a.5.5 0 0 1-.5-.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.499.499 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11z"/>
        </svg>
      </button>
    </div>
  );
}
